import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { resolveImageUrl } from "../lib/imageUrl";
import { 
  Diamond, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Award, 
  Upload, 
  Globe, 
  Sparkles,
  ChevronRight,
  Building2,
  Factory,
  Star,
  Menu,
  X,
  ShoppingBag,
  Gem,
  Crown,
  Heart
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [designDialogOpen, setDesignDialogOpen] = useState(false);
  const [inquiryDialogOpen, setInquiryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form states
  const [designForm, setDesignForm] = useState({
    client_name: "", client_email: "", client_phone: "", company: "",
    design_description: "", material_preference: "", budget_range: ""
  });
  const [designFile, setDesignFile] = useState(null);
  
  const [inquiryForm, setInquiryForm] = useState({
    name: "", email: "", phone: "", company: "", country: "",
    inquiry_type: "export", product_interest: "", quantity_estimate: "", message: ""
  });
  
  const [contactForm, setContactForm] = useState({
    name: "", email: "", phone: "", subject: "", message: ""
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleDesignSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(designForm).forEach(key => formData.append(key, designForm[key]));
      if (designFile) formData.append("file", designFile);
      
      await axios.post(`${API}/design-requests`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Design request submitted successfully!");
      setDesignDialogOpen(false);
      setDesignForm({ client_name: "", client_email: "", client_phone: "", company: "", design_description: "", material_preference: "", budget_range: "" });
      setDesignFile(null);
    } catch (err) {
      toast.error("Failed to submit design request");
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, inquiryForm);
      toast.success("Inquiry submitted successfully! Our team will contact you shortly.");
      setInquiryDialogOpen(false);
      setInquiryForm({ name: "", email: "", phone: "", company: "", country: "", inquiry_type: "export", product_interest: "", quantity_estimate: "", message: "" });
    } catch (err) {
      toast.error("Failed to submit inquiry");
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent successfully!");
      setContactForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-[#C9A962]/20" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="relative">
                <Diamond className="w-10 h-10 text-[#C9A962]" />
                <Sparkles className="w-4 h-4 text-[#C9A962] absolute -top-1 -right-1" />
              </div>
              <div>
                <span className="text-xl font-semibold tracking-wide text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  VK Gems & Jewels
                </span>
                <p className="text-[10px] text-[#8B7355] uppercase tracking-[0.2em]">Since 1995</p>
              </div>
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/about" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors" data-testid="nav-about">About</Link>
              <a href="#collections" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors" data-testid="nav-collections">Collections</a>
              <Link to="/shop" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors" data-testid="nav-shop">Shop</Link>
              <a href="#locations" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors" data-testid="nav-locations">Locations</a>
              <a href="#contact" className="text-sm text-[#B8A88A] hover:text-[#C9A962] transition-colors" data-testid="nav-contact">Contact</a>
              <Link to="/shop" className="px-6 py-2.5 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-medium text-sm hover:from-[#D4B872] hover:to-[#9A7A24] transition-all shadow-lg shadow-[#C9A962]/20" data-testid="nav-shop-cta">
                Shop Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-[#C9A962]" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0A0A] border-t border-[#C9A962]/20 px-6 py-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to="/about" className="text-[#B8A88A] hover:text-[#C9A962]" onClick={() => setMobileMenuOpen(false)}>About</Link>
              <a href="#collections" className="text-[#B8A88A] hover:text-[#C9A962]" onClick={() => setMobileMenuOpen(false)}>Collections</a>
              <Link to="/shop" className="text-[#B8A88A] hover:text-[#C9A962]" onClick={() => setMobileMenuOpen(false)}>Shop</Link>
              <a href="#locations" className="text-[#B8A88A] hover:text-[#C9A962]" onClick={() => setMobileMenuOpen(false)}>Locations</a>
              <a href="#contact" className="text-[#B8A88A] hover:text-[#C9A962]" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Redesigned */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/10475794/pexels-photo-10475794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920" 
            alt="Luxury jewelry"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/95 to-[#0A0A0A]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/50" />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#C9A962]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#7B2D26]/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-gradient-to-r from-[#C9A962] to-transparent" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C9A962]">
                  IGI & GIA Certified
                </span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-white mb-6 leading-[1.1]" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="hero-title">
                Where Timeless
                <span className="block text-[#C9A962]">Elegance</span>
                Meets Artistry
              </h1>
              
              <p className="text-lg text-[#B8A88A] max-w-lg mb-8 font-light leading-relaxed" data-testid="hero-subtitle">
                Discover exquisite handcrafted jewelry and authentic gemstones that bring 
                harmony, healing energies, and positivity to your life. Each piece tells 
                a story of exceptional craftsmanship.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/shop" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold hover:from-[#D4B872] hover:to-[#9A7A24] transition-all shadow-lg shadow-[#C9A962]/30" data-testid="hero-shop-btn">
                  <ShoppingBag className="w-5 h-5" />
                  Explore Collection
                </Link>
                <button onClick={() => setInquiryDialogOpen(true)} className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#C9A962]/50 text-[#C9A962] font-medium hover:bg-[#C9A962]/10 transition-all" data-testid="hero-inquiry-btn">
                  <Globe className="w-5 h-5" />
                  B2B Inquiry
                </button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center gap-8">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#C9A962]" />
                  <span className="text-sm text-[#8B7355]">IGI Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#C9A962]" />
                  <span className="text-sm text-[#8B7355]">GIA Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#C9A962] fill-[#C9A962]" />
                  <span className="text-sm text-[#8B7355]">5-Star Rated</span>
                </div>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#C9A962]/20 p-6 hover:border-[#C9A962]/40 transition-all group">
                <Gem className="w-10 h-10 text-[#C9A962] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Precious Gems</h3>
                <p className="text-sm text-[#8B7355]">Rubies, Emeralds, Sapphires & more</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#C9A962]/20 p-6 hover:border-[#C9A962]/40 transition-all group mt-8">
                <Crown className="w-10 h-10 text-[#C9A962] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Custom Design</h3>
                <p className="text-sm text-[#8B7355]">CAD/CAM bespoke jewelry</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#C9A962]/20 p-6 hover:border-[#C9A962]/40 transition-all group">
                <Heart className="w-10 h-10 text-[#C9A962] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Healing Stones</h3>
                <p className="text-sm text-[#8B7355]">Spiritual energy & harmony</p>
              </div>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#C9A962]/20 p-6 hover:border-[#C9A962]/40 transition-all group mt-8">
                <Globe className="w-10 h-10 text-[#C9A962] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Global Export</h3>
                <p className="text-sm text-[#8B7355]">Worldwide B2B partnerships</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-xs text-[#8B7355] uppercase tracking-widest">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-[#C9A962] to-transparent" />
        </div>
      </section>

      {/* Collections / Products Section */}
      <section id="collections" className="py-24 bg-[#0F0F0F]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#C9A962]" />
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C9A962]">Our Collections</span>
              <div className="h-px w-12 bg-gradient-to-r from-[#C9A962] to-transparent" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Featured <span className="text-[#C9A962]">Products</span>
            </h2>
            <p className="text-[#B8A88A] max-w-2xl mx-auto">
              Discover our handpicked selection of luxury jewelry — from iced-out hip hop chains to
              diamond-studded watches, tennis bracelets, and signature pieces crafted for the modern client.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
            {products.slice(0, 8).map((product) => (
              <Link
                to={`/product/${product.id}`}
                key={product.id}
                className="group relative overflow-hidden bg-[#0A0A0A] border border-[#C9A962]/10 hover:border-[#C9A962]/40 transition-all duration-500"
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-square overflow-hidden relative">
                  <img
                    src={resolveImageUrl(product.image_url)}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                  {product.featured && (
                    <span className="absolute top-3 left-3 px-3 py-1 bg-[#C9A962] text-[#0A0A0A] text-[10px] font-semibold uppercase tracking-wider">
                      Featured
                    </span>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-[10px] text-[#C9A962] uppercase tracking-wider mb-1">{product.category}</p>
                  <h4 className="text-lg text-white mb-2 line-clamp-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {product.name}
                  </h4>
                  <p className="text-[10px] text-[#8B7355] uppercase tracking-wider">From</p>
                  <p className="text-base font-semibold text-[#C9A962]">${(product.lab_grown_price || product.price)?.toLocaleString()}</p>
                </div>
              </Link>
            ))}
          </div>

          {products.length === 0 && (
            <div className="text-center py-16 text-[#8B7355]" data-testid="products-empty">
              Loading products...
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold hover:from-[#D4B872] hover:to-[#9A7A24] transition-all" data-testid="view-full-collection-btn">
              View Full Collection
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-[#7B2D26] via-[#8B3D36] to-[#7B2D26] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#C9A962]/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#C9A962]/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Have a Unique Design in Mind?
          </h2>
          <p className="text-[#F5D0CC] mb-8 max-w-2xl mx-auto">
            Our master craftsmen specialize in bringing your vision to life through custom CAD/CAM jewelry design. 
            Upload your sketches and let us create your dream piece.
          </p>
          <button onClick={() => setDesignDialogOpen(true)} className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#7B2D26] font-semibold hover:bg-[#F5F5F0] transition-all" data-testid="cta-design-btn">
            <Upload className="w-5 h-5" />
            Submit Custom Design Request
          </button>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="py-24 bg-[#0A0A0A]" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#C9A962]" />
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C9A962]">Visit Us</span>
              <div className="h-px w-12 bg-gradient-to-r from-[#C9A962] to-transparent" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Our <span className="text-[#C9A962]">Locations</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Manufacturing Hub */}
            <div className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-hidden group" data-testid="location-manufacturing">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1715374033196-0ff662284a7e?crop=entropy&cs=srgb&fm=jpg&w=800"
                  alt="Manufacturing facility"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] to-transparent" />
                <div className="absolute top-4 left-4 px-4 py-2 bg-[#C9A962] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider">
                  Manufacturing Hub
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <Factory className="w-6 h-6 text-[#C9A962] shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl text-white mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Srikakulam, Andhra Pradesh</h3>
                    <p className="text-sm text-[#8B7355]">Main Production & Fulfillment Center</p>
                  </div>
                </div>
                <div className="space-y-3 text-[#B8A88A] text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#C9A962] shrink-0 mt-0.5" />
                    <p>2-216, Kothapeta Junction, Kotabommali Mandal, Srikakulam District, AP - 532195</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[#C9A962] shrink-0" />
                    <p>Daily: 9:00 AM - 8:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Corporate Office */}
            <div className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-hidden group" data-testid="location-corporate">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80"
                  alt="Corporate office"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] to-transparent" />
                <div className="absolute top-4 left-4 px-4 py-2 bg-[#C9A962] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider">
                  Corporate Office
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <Building2 className="w-6 h-6 text-[#C9A962] shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl text-white mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Gurgaon, Haryana</h3>
                    <p className="text-sm text-[#8B7355]">International Liaison & B2B Hub</p>
                  </div>
                </div>
                <div className="space-y-3 text-[#B8A88A] text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#C9A962] shrink-0 mt-0.5" />
                    <p>2nd Floor, Vatika India First Place, MG Road, Gurgaon, Haryana - 122002</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[#C9A962] shrink-0" />
                    <p>Daily: 9:00 AM - 8:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* USA Sales Office */}
            <div className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-hidden group" data-testid="location-usa">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1496588152823-86ff7695e68f?auto=format&fit=crop&w=800&q=80"
                  alt="USA Sales Office"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] to-transparent" />
                <div className="absolute top-4 left-4 px-4 py-2 bg-[#C9A962] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider">
                  USA Sales Office
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <Building2 className="w-6 h-6 text-[#C9A962] shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl text-white mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Lincoln, Nebraska — USA</h3>
                    <p className="text-sm text-[#8B7355]">North American Sales & Distribution</p>
                  </div>
                </div>
                <div className="space-y-3 text-[#B8A88A] text-sm">
                  <p className="text-[#F5F5F0] font-medium" data-testid="usa-office-name">Bodacias Kalinga LLC</p>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#C9A962] shrink-0 mt-0.5" />
                    <p data-testid="usa-office-address">200 S 21st St STE 400A, Lincoln, NE 68510</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-4 h-4 text-[#C9A962] shrink-0 mt-0.5" />
                    <p data-testid="usa-office-ein">EIN No: 99-3378525</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#0F0F0F]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-12 bg-gradient-to-r from-[#C9A962] to-transparent" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C9A962]">Get In Touch</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Let's Create<br /><span className="text-[#C9A962]">Something Beautiful</span>
              </h2>
              <p className="text-[#B8A88A] mb-10 leading-relaxed">
                Whether you're looking for the perfect piece or have a custom design in mind, 
                our team is here to help you every step of the way.
              </p>

              <div className="space-y-6">
                <a href="tel:+919555337978" className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#C9A962]/20 hover:border-[#C9A962]/40 transition-all group" data-testid="contact-phone-link">
                  <div className="w-12 h-12 bg-[#C9A962]/10 flex items-center justify-center group-hover:bg-[#C9A962]/20 transition-colors">
                    <Phone className="w-5 h-5 text-[#C9A962]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#8B7355] uppercase tracking-wider">Call Us</p>
                    <p className="text-lg text-white">+91 9555337978</p>
                  </div>
                </a>

                <a href="mailto:info@vkgemsandjewels.com" className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#C9A962]/20 hover:border-[#C9A962]/40 transition-all group" data-testid="contact-email-link">
                  <div className="w-12 h-12 bg-[#C9A962]/10 flex items-center justify-center group-hover:bg-[#C9A962]/20 transition-colors">
                    <Mail className="w-5 h-5 text-[#C9A962]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#8B7355] uppercase tracking-wider">Email Us</p>
                    <p className="text-lg text-white">info@vkgemsandjewels.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[#C9A962]/20">
                  <div className="w-12 h-12 bg-[#C9A962]/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#C9A962]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#8B7355] uppercase tracking-wider">Operating Hours</p>
                    <p className="text-lg text-white">Daily: 9:00 AM - 8:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Google Rating */}
              <div className="mt-8 p-6 bg-[#0A0A0A] border border-[#C9A962]/20">
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`gmb-star-${i}`} className="w-5 h-5 text-[#C9A962] fill-[#C9A962]" />
                  ))}
                  <span className="text-white ml-2 text-lg">5.0</span>
                </div>
                <p className="text-sm text-[#8B7355]">Rated 5 stars on Google My Business</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#0A0A0A] border border-[#C9A962]/20 p-8">
              <h3 className="text-2xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Send us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#8B7355] uppercase tracking-wider block mb-2">Name *</label>
                    <input type="text" required value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962] transition-colors" data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="text-xs text-[#8B7355] uppercase tracking-wider block mb-2">Email *</label>
                    <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962] transition-colors" data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#8B7355] uppercase tracking-wider block mb-2">Phone</label>
                  <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962] transition-colors" data-testid="contact-phone" />
                </div>
                <div>
                  <label className="text-xs text-[#8B7355] uppercase tracking-wider block mb-2">Subject *</label>
                  <input type="text" required value={contactForm.subject} onChange={(e) => setContactForm({...contactForm, subject: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962] transition-colors" data-testid="contact-subject" />
                </div>
                <div>
                  <label className="text-xs text-[#8B7355] uppercase tracking-wider block mb-2">Message *</label>
                  <textarea required rows={4} value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962] transition-colors resize-none" data-testid="contact-message" />
                </div>
                <button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold hover:from-[#D4B872] hover:to-[#9A7A24] transition-all" data-testid="contact-submit">
                  {loading ? <span className="spinner mx-auto" /> : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] border-t border-[#C9A962]/20" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Diamond className="w-8 h-8 text-[#C9A962]" />
                <span className="text-xl font-semibold text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  VK Gems & Jewels
                </span>
              </div>
              <p className="text-[#8B7355] mb-4 max-w-md">
                Crafting excellence in gems and jewelry since 1995. IGI & GIA certified manufacturer 
                bringing harmony and beauty to the world.
              </p>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#C9A962]" />
                <a href="tel:+919555337978" className="text-[#B8A88A] hover:text-[#C9A962] transition-colors">+91 9555337978</a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/shop" className="block text-[#8B7355] hover:text-[#C9A962] transition-colors">Shop</Link>
                <a href="#collections" className="block text-[#8B7355] hover:text-[#C9A962] transition-colors">Collections</a>
                <Link to="/about" className="block text-[#8B7355] hover:text-[#C9A962] transition-colors">About Us</Link>
                <a href="#contact" className="block text-[#8B7355] hover:text-[#C9A962] transition-colors">Contact</a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">Services</h4>
              <div className="space-y-2">
                <button onClick={() => setInquiryDialogOpen(true)} className="block text-[#8B7355] hover:text-[#C9A962] transition-colors">B2B Export</button>
                <button onClick={() => setDesignDialogOpen(true)} className="block text-[#8B7355] hover:text-[#C9A962] transition-colors">Custom Design</button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-[#C9A962]/10">
            <p className="text-sm text-[#8B7355]">
              © 2025 VK Gems & Jewels. All rights reserved. IGI & GIA Certified Manufacturer.
            </p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="#" className="text-sm text-[#8B7355] hover:text-[#C9A962] transition-colors">Privacy Policy</a>
              <a href="#" className="text-sm text-[#8B7355] hover:text-[#C9A962] transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Inquiry Dialog */}
      <Dialog open={inquiryDialogOpen} onOpenChange={setInquiryDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#C9A962]/20 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inquiry-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Global Export Inquiry</DialogTitle>
            <DialogDescription className="text-[#8B7355]">Fill out the form below to start your export partnership.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInquirySubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <input type="text" placeholder="Your Name *" required value={inquiryForm.name} onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="inquiry-name" />
              <input type="email" placeholder="Email *" required value={inquiryForm.email} onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="inquiry-email" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="tel" placeholder="Phone *" required value={inquiryForm.phone} onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="inquiry-phone" />
              <input type="text" placeholder="Company" value={inquiryForm.company} onChange={(e) => setInquiryForm({...inquiryForm, company: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="inquiry-company" />
            </div>
            <input type="text" placeholder="Country *" required value={inquiryForm.country} onChange={(e) => setInquiryForm({...inquiryForm, country: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="inquiry-country" />
            <select value={inquiryForm.inquiry_type} onChange={(e) => setInquiryForm({...inquiryForm, inquiry_type: e.target.value})} className="w-full bg-[#0F0F0F] border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="inquiry-type">
              <option value="export">International Export</option>
              <option value="wholesale">Domestic Wholesale</option>
            </select>
            <input type="text" placeholder="Product Interest *" required value={inquiryForm.product_interest} onChange={(e) => setInquiryForm({...inquiryForm, product_interest: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="inquiry-product" />
            <textarea placeholder="Your Message *" required rows={3} value={inquiryForm.message} onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962] resize-none" data-testid="inquiry-message" />
            <button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold" data-testid="inquiry-submit">
              {loading ? <span className="spinner mx-auto" /> : "Submit Inquiry"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Design Request Dialog */}
      <Dialog open={designDialogOpen} onOpenChange={setDesignDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#C9A962]/20 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="design-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Custom Design Request</DialogTitle>
            <DialogDescription className="text-[#8B7355]">Upload your design sketches for CAD/CAM manufacturing.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDesignSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <input type="text" placeholder="Your Name *" required value={designForm.client_name} onChange={(e) => setDesignForm({...designForm, client_name: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="design-name" />
              <input type="email" placeholder="Email *" required value={designForm.client_email} onChange={(e) => setDesignForm({...designForm, client_email: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="design-email" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input type="tel" placeholder="Phone *" required value={designForm.client_phone} onChange={(e) => setDesignForm({...designForm, client_phone: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="design-phone" />
              <input type="text" placeholder="Company" value={designForm.company} onChange={(e) => setDesignForm({...designForm, company: e.target.value})} className="bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="design-company" />
            </div>
            <textarea placeholder="Design Description *" required rows={3} value={designForm.design_description} onChange={(e) => setDesignForm({...designForm, design_description: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962] resize-none" data-testid="design-description" />
            <select value={designForm.material_preference} onChange={(e) => setDesignForm({...designForm, material_preference: e.target.value})} className="w-full bg-[#0F0F0F] border-b border-[#C9A962]/30 text-white py-3 focus:outline-none focus:border-[#C9A962]" data-testid="design-material">
              <option value="">Select Material Preference</option>
              <option value="gold_22k">22K Gold</option>
              <option value="gold_18k">18K Gold</option>
              <option value="platinum">Platinum</option>
              <option value="silver">Silver</option>
            </select>
            <div className="border-2 border-dashed border-[#C9A962]/30 p-6 text-center hover:border-[#C9A962]/50 transition-colors">
              <label className="flex flex-col items-center cursor-pointer">
                <Upload className="w-8 h-8 text-[#C9A962] mb-2" />
                <span className="text-sm text-[#8B7355]">Upload Design Sketch</span>
                <span className="text-xs text-[#8B7355]/60 mt-1">PDF, JPG, PNG, DXF, STL accepted</span>
                <input type="file" className="hidden" onChange={(e) => setDesignFile(e.target.files[0])} accept=".pdf,.jpg,.jpeg,.png,.dxf,.dwg,.stl" data-testid="design-file" />
                {designFile && <span className="text-xs text-[#C9A962] mt-2">{designFile.name}</span>}
              </label>
            </div>
            <button type="submit" disabled={loading} className="w-full py-4 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold" data-testid="design-submit">
              {loading ? <span className="spinner mx-auto" /> : "Submit Design Request"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
